import React from "react";
import {
  FileIcon,
  ImageIcon,
  FileTextIcon,
  FileSpreadsheetIcon,
  ShapesIcon as FilePresentationIcon,
  FileArchiveIcon,
  XIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export interface FileAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
}

interface FileAttachmentPreviewProps {
  file: FileAttachment;
  onRemove?: (id: string) => void;
  showRemoveButton?: boolean;
}

export default function FileAttachmentPreview({
  file,
  onRemove,
  showRemoveButton = true,
}: FileAttachmentPreviewProps) {
  const isImage = file.type.startsWith("image/");
  const isDocument =
    file.type.includes("pdf") ||
    file.type.includes("doc") ||
    file.type.includes("txt");
  const isSpreadsheet =
    file.type.includes("sheet") ||
    file.type.includes("excel") ||
    file.type.includes("csv");
  const isPresentation =
    file.type.includes("presentation") || file.type.includes("powerpoint");
  const isArchive =
    file.type.includes("zip") ||
    file.type.includes("rar") ||
    file.type.includes("tar");

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Get appropriate icon based on file type
  const getFileIcon = () => {
    if (isImage) return <ImageIcon className="h-5 w-5 text-blue-500" />;
    if (isDocument) return <FileTextIcon className="h-5 w-5 text-red-500" />;
    if (isSpreadsheet)
      return <FileSpreadsheetIcon className="h-5 w-5 text-green-500" />;
    if (isPresentation)
      return <FilePresentationIcon className="h-5 w-5 text-orange-500" />;
    if (isArchive)
      return <FileArchiveIcon className="h-5 w-5 text-purple-500" />;
    return <FileIcon className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="flex items-center gap-2 p-2 rounded-md bg-secondary/50 max-w-full">
      {isImage && file.url ? (
        <div className="h-10 w-10 rounded overflow-hidden flex-shrink-0">
          <img
            src={file.url}
            alt={file.name}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="h-10 w-10 rounded bg-secondary flex items-center justify-center flex-shrink-0">
          {getFileIcon()}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={file.name}>
          {file.name}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatFileSize(file.size)}
        </p>
      </div>

      {showRemoveButton && onRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onRemove(file.id)}
        >
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Remove file</span>
        </Button>
      )}
    </div>
  );
}
